import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { sellerApi } from '../../utils/retailSalesApi';
import { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const sellerSchema = yup.object({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  mobile: yup
    .string()
    .required('Mobile number is required')
    .matches(/^[6-9]\d{9}$/, 'Mobile must be a valid 10-digit number'),
  email: yup.string().email('Invalid email format').optional(),
  address: yup.string().optional(),
});

interface SellerFormData {
  name: string;
  mobile: string;
  email?: string;
  address?: string;
}

const SellerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState('');
  const [mobileError, setMobileError] = useState('');

  const form = useForm<SellerFormData>({
    resolver: yupResolver(sellerSchema) as any,
    defaultValues: {
      name: '',
      mobile: '',
      email: '',
      address: '',
    },
  });

  useEffect(() => {
    if (id && selectedLocation?.id) {
      loadSeller();
    }
  }, [id, selectedLocation?.id]);

  const loadSeller = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const seller = await sellerApi.get(id);
      form.reset({
        name: seller.name,
        mobile: seller.mobile,
        email: seller.email || '',
        address: seller.address || '',
      });
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load seller'));
    } finally {
      setLoading(false);
    }
  };

  const validateMobileUniqueness = async (mobile: string) => {
    if (!selectedLocation?.id || !mobile) return;
    
    try {
      const sellers = await sellerApi.list(selectedLocation.id, mobile);
      const existingSeller = sellers.find(s => s.mobile === mobile && s.id !== id);
      if (existingSeller) {
        setMobileError('Mobile number already exists for another seller');
        return false;
      }
      setMobileError('');
      return true;
    } catch (err) {
      // If search fails, allow submission (backend will validate)
      return true;
    }
  };

  const handleMobileBlur = async () => {
    const mobile = form.getValues('mobile');
    if (mobile) {
      await validateMobileUniqueness(mobile);
    }
  };

  const onSubmit = async (data: SellerFormData) => {
    if (!selectedLocation?.id) {
      setError('Please select a location');
      return;
    }

    // Validate mobile uniqueness
    const isUnique = await validateMobileUniqueness(data.mobile);
    if (!isUnique) {
      return;
    }

    try {
      setError('');
      setMobileError('');

      if (id) {
        await sellerApi.update(id, data);
      } else {
        await sellerApi.create({
          ...data,
          location_id: selectedLocation.id,
        });
      }

      navigate('/retail-sales/sellers');
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'Failed to save seller');
      setError(errorMsg);
      
      // Check if error is about mobile uniqueness
      if (errorMsg.toLowerCase().includes('mobile') && errorMsg.toLowerCase().includes('exists')) {
        setMobileError(errorMsg);
      }
    }
  };

  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for fish farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/retail-sales/sellers" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Edit Seller' : 'Add Seller'}
          </h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={form.handleSubmit(onSubmit)} className="card p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="form-label">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...form.register('name')}
              className={`form-input ${form.formState.errors.name ? 'border-red-500' : ''}`}
            />
            {form.formState.errors.name && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="form-label">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...form.register('mobile')}
              onBlur={handleMobileBlur}
              className={`form-input ${form.formState.errors.mobile || mobileError ? 'border-red-500' : ''}`}
              placeholder="10-digit mobile number"
            />
            {form.formState.errors.mobile && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.mobile.message}</p>
            )}
            {mobileError && (
              <p className="mt-1 text-sm text-red-600">{mobileError}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              {...form.register('email')}
              className={`form-input ${form.formState.errors.email ? 'border-red-500' : ''}`}
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-sm text-red-600">{form.formState.errors.email.message}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="form-label">Address</label>
            <textarea
              {...form.register('address')}
              rows={3}
              className="form-input"
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4 pt-4 border-t">
          <Link
            to="/retail-sales/sellers"
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="btn-primary"
          >
            {form.formState.isSubmitting ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </span>
            ) : (
              id ? 'Update Seller' : 'Create Seller'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SellerForm;

